import React, { Dispatch, SetStateAction } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Typography, Link } from "@mui/joy";

import PrimeTable from "./PrimeTable";
import { getTableDimensions } from "../util/elementUtil";
import ReferenceLink from "./ReferenceLink";
import { ReferenceProcessor } from "../util/references";
import { FileViewerArgs } from "./fileviewer/FileViewer";

interface MarkdownRendererProps {
    message: string;
    contentId: string;
    showTableDownloadCopyButtons?: boolean;
    processLinks?: boolean;
    setCurrentFileViewerFile?: Dispatch<SetStateAction<FileViewerArgs | null>>;
    currentFileViewerFile?: FileViewerArgs | null;
    referenceProcessor?: ReferenceProcessor;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
    message,
    contentId,
    currentFileViewerFile = null,
    setCurrentFileViewerFile = null,
    processLinks = true,
    referenceProcessor = null,
    showTableDownloadCopyButtons = true,
}) => {
    if (referenceProcessor && !setCurrentFileViewerFile) {
        throw new Error("setCurrentFileViewerFile is required when using referenceProcessor");
    }

    return (
        <Markdown
            key={`markdown-content-${contentId}`}
            remarkPlugins={[remarkGfm]}
            components={{
                table(props) {
                    const { rows, columns } = getTableDimensions(props);
                    const key = `table-${contentId}-${rows}-${columns}`;
                    return (
                        <PrimeTable key={key} showDownloadCopy={showTableDownloadCopyButtons}>
                            {props.children}
                        </PrimeTable>
                    );
                },
                p(props) {
                    return <Typography>{props.children}</Typography>;
                },
                pre(props) {
                    return <Typography>{props.children}</Typography>;
                },
                a(props) {
                    const { href, children } = props;

                    if (!processLinks || !referenceProcessor || !setCurrentFileViewerFile) {
                        // Not processing links, just return the children
                        return <>[{children}]</>;
                    }

                    if (!href?.startsWith("#REFERENCE")) {
                        return (
                            <Link key={href} href={href}>
                                {children}
                            </Link>
                        );
                    }

                    const referenceKey = children?.toString();
                    return (
                        <ReferenceLink
                            key={href}
                            referenceKey={referenceKey!}
                            processor={referenceProcessor}
                            currentFileViewerFile={currentFileViewerFile}
                            setCurrentFileViewerFile={setCurrentFileViewerFile}
                        />
                    );
                },
            }}
        >
            {message}
        </Markdown>
    );
};
